<template>
  <div id="app">
    <mds-page-shell
      :logo="logoPath"
      :logo-responsive="logoPath"
      logo-alt="Morningstar | DBRS"
    >
      <template v-if="$route.path !== '/'" #mds-page-shell-masthead-right>
        <mds-button
          variation="flat"
          icon="person"
          flat-button-icon-size="medium"
          @click.prevent="handleSignOut"
        >
          Sign Out
        </mds-button>
      </template>
      <template #default>
        <div class="content-wrapper">
          <router-view :key="$route.fullPath" />
        </div>
        <div class="footer">
          <footer>
            <span class="copy-right"
              >All Contents &#169; {{ currentYear }} Morningstar DBRS</span
            >
            <span class="footer-links">
              <a
                href="https://dbrs.morningstar.com/terms-and-conditions/"
                class="my-link"
                target="_blank"
              >
                Terms and Conditions
              </a>
              |
              <a
                href="https://dbrs.morningstar.com/privacy-policy/"
                class="my-link"
                target="_blank"
              >
                Privacy Policy
              </a>
              |
              <a
                href="https://dbrs.morningstar.com/disclaimer/"
                class="my-link"
                target="_blank"
              >
                Disclaimers
              </a>
              |
              <a
                href="https://dbrs.morningstar.com/proprietary-rights/"
                class="my-link"
                target="_blank"
              >
                Proprietary Rights
              </a>
              |
              <a
                href="https://dbrs.morningstar.com/regulatory/"
                class="my-link"
                target="_blank"
              >
                Regulatory Affairs
              </a>
            </span>
          </footer>
        </div>
      </template>
    </mds-page-shell>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import MdsPageShell from '@mds/page-shell-vue3'
import { MdsButton } from '@mds/button-vue3'
import logo from '@/assets/logo.svg'
import { logout } from '@/apis/user-authentication'

// Reactive state
const logoPath = ref(logo)
const currentYear = ref('')

// Composables
const route = useRoute()
const { proxy } = getCurrentInstance()
const $gtag = proxy.$gtag

// Computed
const isSignOutVisible = computed(() => route.path !== '/')

// Lifecycle
onMounted(() => {
  currentYear.value = new Date().getFullYear()
})

// Methods
const handleSignOut = () => {
  try {
    const user = JSON.parse(localStorage.getItem('userData') || '{}')
    localStorage.removeItem('userData')
    
    // Analytics tracking
    $gtag.customMap({ dimension2: 'username', dimension3: 'email' })
    $gtag.event('logout', {
      event_category: 'engagement',
      event_label: 'user logout',
      value: user.id,
      username: user.username,
      email: user.email,
    })
    
    const successUrl = new URL(window.location.origin)
    logout(successUrl)
  } catch (error) {
    console.error('Sign out error:', error)
    // Fallback logout
    const successUrl = new URL(window.location.origin)
    logout(successUrl)
  }
}
</script>

<style lang="scss">
@import "@/styles/reset";
@import "@/styles/constants";

html {
  box-sizing: border-box;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

body {
  height: 100vh;
  min-width: $app-min-width;
  overflow-y: hidden;
  font-family: $mds-typography-font-family-sans-serif !important;
}

// hide "show password" icon on ms browsers so we can use the MDS one instead
// https://developer.mozilla.org/en-US/docs/Web/CSS/::-ms-reveal
input::-ms-reveal {
  display: none;
}

// strengthen specificity of the hidden attribute
[hidden] {
  display: none !important;
}

h1 {
  @include mds-level-1-heading();
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  height: calc(100vh - #{$mds-masthead-height} - 30px);
  overflow-y: auto;
}

footer {
  height: 30px;
  line-height: 30px;
  font-size: 12px;
  border-top: solid 1px #e5e5e5;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  background-color: #fff;

  .my-link {
    @include mds-link($underline: false, $visited-styles: false);
    color: #007cc2;
  }

  .copy-right {
    @include mds-body-text-s();
    float: left;
    margin-left: 15px;
    font-weight: 300;
    line-height: 30px;
  }

  .footer-links {
    float: right;
    margin-right: 15px;
  }
}
</style>

<style lang="scss">
.mds-page-shell__masthead-logo,
.mds-page-shell__masthead-logo-wide {
  max-height: 42px;
}
</style>
